import { Stack, Typography } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { forwardRef, useCallback, useEffect, useImperativeHandle } from 'react';
import type { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import settings from '/@/core/settings';
import { _ } from '/@/locale';
import { MuiSelect } from '/@/mui/select';
import { setPreferences } from '/@/store/session/actions';
import { getSessionUnits } from '/@/store/session/selectors';
import type { ProfileV2FormData } from '../..';
import FormSection from '../../components/form-section';
import TabContainer from '../../components/tab-container';

export interface UnitsFormData {
    system: string;
    area: string;
    cornYield: string;
    cornDensity: string;
    soyYield: string;
    soyDensity: string;
    winterCerealYield: string;
    winterCerealDensity: string;
}
const UnitsEditTab = forwardRef(({ form }: { form: ReturnType<typeof useForm<ProfileV2FormData>> }, ref) => {
    const dispatch = useDispatch();
    const units = useSelector(getSessionUnits);

    useEffect(() => {
        form.reset({
            ...form.getValues(),
            system: units.system,
            area: units.area,
            cornYield: units.cornYield,
            cornDensity: units.cornDensity,
            soyYield: units.soyYield,
            soyDensity: units.soyDensity,
            winterCerealYield: units.winterCerealYield ?? '',
            winterCerealDensity: units.winterCerealDensity ?? '',
        });
    }, [units, form]);

    const onSubmit = useCallback(
        async (data: UnitsFormData) => {
            await dispatch(
                setPreferences({
                    units: {
                        system: data.system,
                        area: data.area,
                        cornYield: data.cornYield,
                        cornDensity: data.cornDensity,
                        soyYield: data.soyYield,
                        soyDensity: data.soyDensity,
                        winterCerealYield: data.winterCerealYield,
                        winterCerealDensity: data.winterCerealDensity,
                    },
                    interactive: true,
                })
            );
        },
        [dispatch]
    );

    useImperativeHandle(
        ref,
        () => ({
            onSubmit,
        }),
        [onSubmit]
    );

    return (
        <TabContainer title={_('Unidades de medición')}>
            <Grid2 container columns={9}>
                <Grid2 xs={12} lg={6}>
                    <Stack spacing={4}>
                        <Stack spacing={2}>
                            <FormSection title={_('Sistema de medición')}>
                                <Grid2 xs={12} md={6}>
                                    <MuiSelect
                                        inputLabel={_('Sistema')}
                                        value={form.watch('system')}
                                        {...form.register('system', {
                                            required: _('El sistema de medición es requerido'),
                                        })}
                                        options={settings.choices?.unitSystems || []}
                                        error={!!form.formState.errors.system}
                                        formHelperText={form.formState.errors.system?.message}
                                        formControlProps={{ fullWidth: true }}
                                    />
                                </Grid2>
                                <Grid2 xs={12} md={6}>
                                    <MuiSelect
                                        inputLabel={_('Área')}
                                        value={form.watch('area')}
                                        {...form.register('area', {
                                            required: _('La unidad de área es requerida'),
                                        })}
                                        options={settings.choices?.areaUnits || []}
                                        error={!!form.formState.errors.area}
                                        formHelperText={form.formState.errors.area?.message}
                                        formControlProps={{ fullWidth: true }}
                                    />
                                </Grid2>
                            </FormSection>

                            <FormSection title={_('Unidades de medida')}>
                                <Grid2 xs={12}>
                                    <Typography variant='subtitle1' color='primary'>
                                        {_('Maíz')}
                                    </Typography>
                                </Grid2>
                                <Grid2 xs={12} md={6}>
                                    <MuiSelect
                                        inputLabel={_('Rendimiento')}
                                        value={form.watch('cornYield')}
                                        {...form.register('cornYield', {
                                            required: _('La unidad de rendimiento de maíz es requerida'),
                                        })}
                                        options={settings.choices?.yieldUnits || []}
                                        error={!!form.formState.errors.cornYield}
                                        formHelperText={form.formState.errors.cornYield?.message}
                                        formControlProps={{ fullWidth: true }}
                                    />
                                </Grid2>
                                <Grid2 xs={12} md={6}>
                                    <MuiSelect
                                        inputLabel={_('Densidad')}
                                        value={form.watch('cornDensity')}
                                        {...form.register('cornDensity', {
                                            required: _('La unidad de densidad de maíz es requerida'),
                                        })}
                                        options={settings.choices?.cornDensityUnits || []}
                                        error={!!form.formState.errors.cornDensity}
                                        formHelperText={form.formState.errors.cornDensity?.message}
                                        formControlProps={{ fullWidth: true }}
                                    />
                                </Grid2>

                                <Grid2 xs={12}>
                                    <Typography variant='subtitle1' color='primary'>
                                        {_('Soja')}
                                    </Typography>
                                </Grid2>
                                <Grid2 xs={12} md={6}>
                                    <MuiSelect
                                        inputLabel={_('Rendimiento')}
                                        value={form.watch('soyYield')}
                                        {...form.register('soyYield', {
                                            required: _('La unidad de rendimiento de soja es requerida'),
                                        })}
                                        options={settings.choices?.yieldUnits || []}
                                        error={!!form.formState.errors.soyYield}
                                        formHelperText={form.formState.errors.soyYield?.message}
                                        formControlProps={{ fullWidth: true }}
                                    />
                                </Grid2>
                                <Grid2 xs={12} md={6}>
                                    <MuiSelect
                                        inputLabel={_('Densidad')}
                                        value={form.watch('soyDensity')}
                                        {...form.register('soyDensity', {
                                            required: _('La unidad de densidad de soja es requerida'),
                                        })}
                                        options={settings.choices?.soyDensityUnits || []}
                                        error={!!form.formState.errors.soyDensity}
                                        formHelperText={form.formState.errors.soyDensity?.message}
                                        formControlProps={{ fullWidth: true }}
                                    />
                                </Grid2>

                                <Grid2 xs={12}>
                                    <Typography variant='subtitle1' color='primary'>
                                        {_('Cereales de invierno')}
                                    </Typography>
                                </Grid2>
                                <Grid2 xs={12} md={6}>
                                    <MuiSelect
                                        inputLabel={_('Rendimiento')}
                                        value={form.watch('winterCerealYield') ?? ''}
                                        {...form.register('winterCerealYield', {
                                            required: _(
                                                'La unidad de rendimiento de cereales de invierno es requerida'
                                            ),
                                        })}
                                        options={settings.choices?.yieldUnits || []}
                                        error={!!form.formState.errors.winterCerealYield}
                                        formHelperText={form.formState.errors.winterCerealYield?.message}
                                        formControlProps={{ fullWidth: true }}
                                    />
                                </Grid2>
                                <Grid2 xs={12} md={6}>
                                    <MuiSelect
                                        inputLabel={_('Densidad')}
                                        value={form.watch('winterCerealDensity') ?? ''}
                                        {...form.register('winterCerealDensity', {
                                            required: _('La unidad de densidad de cereales de invierno es requerida'),
                                        })}
                                        options={settings.choices?.winterCerealDensityUnits || []}
                                        error={!!form.formState.errors.winterCerealDensity}
                                        formHelperText={form.formState.errors.winterCerealDensity?.message}
                                        formControlProps={{ fullWidth: true }}
                                    />
                                </Grid2>
                            </FormSection>
                        </Stack>
                    </Stack>
                </Grid2>
            </Grid2>
        </TabContainer>
    );
});

export default UnitsEditTab;
